// Build a line, or take it back down — from the UI.
//
// Until now both were API-only. Everything proven on 2026-08-04 — a line built across
// FreePBX, GDMS and Commio, torn down, deleted, re-added and rebuilt — was driven by
// hand-written fetch calls, which meant Verso could provision a line and an operator
// could not. This is that gap closed.
//
// ONE COMPONENT FOR BOTH DIRECTIONS, because they are the same conversation: look at
// what would happen, then decide. The differences are the endpoints, the verb, and that
// a teardown is the one that stops a phone working, so it says so louder.
//
// TWO PHASES, never collapsed into one:
//
//   plan    GET .../plan — read-only, reaches nothing. Blockers first (a run that cannot
//           start is the most useful thing to say), then the ordered steps grouped by
//           system, then notes. This is the promise the add-line wizard makes when it
//           says provisioning is a separate step.
//   result  POST — per-step outcomes and the read-back verification, in the vocabulary
//           the server already uses: ok / error / frozen / skipped, and
//           confirmed / mismatch / unavailable.
//
// The one rule this UI must not break: **never render a step as done because the request
// was accepted.** `ok` is what the server said about the write; the verification line
// underneath is whether anyone went and looked. Suspension reported success for months
// because those two were the same pixel.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, RUN_TIMEOUT_MS } from '../../lib/api.js';
import LoadingSpinner from '../shared/LoadingSpinner.jsx';

const SYSTEM_LABEL = {
  pbx: 'PBX',
  gdms: 'Device (GDMS)',
  commio: 'Carrier (Commio)',
  verso: 'Verso',
};

// Status → how it reads. `frozen`, `skipped` and `not-selected` are deliberately NOT ticks and
// deliberately not crosses: "we chose not to touch a live phone system", "something upstream
// stopped it" and "you left this system out" are three different things, and none of them is
// success or failure.
//
// `not-selected` is grey and quiet. Kevin ran a carrier-only build on Carole Carroll's line and
// asked "troubleshoot why the build didn't complete without errors" — it had completed, and every
// step he had UNCHECKED was reported in amber with a "!". A run that does exactly what was asked
// must not look half broken, or the marks stop meaning anything (2026-08-19).
const STEP_STYLE = {
  ok:        { mark: '✓', cls: 'text-green-700',  label: 'done' },
  error:     { mark: '✕', cls: 'text-red-700',    label: 'failed' },
  frozen:    { mark: '⊘', cls: 'text-slate-500',  label: 'refused — nothing sent' },
  skipped:   { mark: '!', cls: 'text-amber-700',  label: 'not attempted' },
  'not-selected': { mark: '–', cls: 'text-gray-400', label: 'not included in this run' },
  // In flight. The mark is a spinner rather than a tick-in-waiting, because a row that looks
  // finished-but-grey is the one thing worse than no progress at all.
  running:   { mark: '◌', cls: 'text-blue-700 animate-pulse', label: 'running…' },
  // Reported by the executor and deliberately never drawn — see the filter in StepList.
  'not-needed': { mark: '–', cls: 'text-gray-400', label: 'not needed' },
  // A JOB, NOT A RESULT. A Calix takes its SIP credentials and dial plan by hand, so this step is
  // listed and never run. Amber and an explicit label rather than a grey dash, because the operator
  // has to go and do it and a quiet dash is how that gets missed — and never a tick, because
  // nothing has happened yet.
  // A JOB, NOT A RESULT. A Calix takes its SIP credentials and dial plan by hand, so the step is
  // listed and never run. Amber and spelled out rather than a grey "manual", because the operator
  // has to go and do it and the quiet version is how that gets missed — and never a tick, because
  // nothing has happened yet.
  manual:    { mark: '☐', cls: 'text-amber-600',  label: 'yours to do by hand' },
  'already-applied': { mark: '✓', cls: 'text-green-700', label: 'already done' },
};

const CHECK_STYLE = {
  confirmed:   { cls: 'text-green-700', label: 'confirmed by reading it back' },
  mismatch:    { cls: 'text-red-700',   label: 'the system disagrees' },
  unavailable: { cls: 'text-amber-700', label: 'could not check — not a pass' },
};

/**
 * @param mode      'provision' | 'teardown'
 * @param line      the line row (for the heading)
 * @param onClose   dismiss
 * @param onDone    called after a run, so the caller can refetch
 */
export default function LineRunDialog({ mode, line, onClose, onDone }) {
  const qc = useQueryClient();
  const teardown = mode === 'teardown';
  const [result, setResult] = useState(null);

  // PER-SYSTEM CONSENT, inherited from the subscriber-wide dialog this replaced. It is not
  // decoration: these are live writes to a customer's PBX, their handset and a carrier, and
  // feedback_no_external_writes asks for each to be consented rather than implied. All on
  // by default — the operator opened this to make the line work — but each is visible and
  // switchable, because "push everything except the carrier" is a real thing to want when
  // one of the three is misbehaving.
  //
  // 'verso' is deliberately NOT offered. Consent exists for systems we do not own; our own
  // rows are always written, and a tick the API ignores would be a lie about what happens.
  const [allowed, setAllowed] = useState({ pbx: true, gdms: true, commio: true });
  // THE PLAN THAT RAN, not the one that exists now.
  //
  // The report used to render `plan.data`, which is REFETCHED after the run — so it described
  // the next plan while claiming to describe the last run, and got it wrong in both
  // directions. Live on +13038235285 (2026-08-18): `set-commio-route` succeeded, the carrier
  // route was therefore correct, the planner stopped emitting it, and the step VANISHED from
  // its own run report — the steps that work are exactly the ones that disappear, and that one
  // was the whole reason inbound calls were broken. Meanwhile a caller-ID step newly present
  // in the post-run plan had no outcome to overlay and rendered as a bare grey dot inside a
  // report headlined "Built, and every check confirmed it".
  const [ranPlan, setRanPlan] = useState(null);

  const planPath = teardown
    ? `/lines/${line.id}/teardown/plan`
    : `/lines/${line.id}/provision/plan`;

  const plan = useQuery({
    queryKey: ['line', line.id, mode, 'plan'],
    queryFn: () => api.get(planPath),
    // A plan is a snapshot of live state — never served from cache, or an operator
    // approves a picture of five minutes ago.
    staleTime: 0,
    gcTime: 0,
  });

  // WHAT THE RUN IS DOING, WHILE IT DOES IT. Kevin, 2026-08-20: "as each task is run, show progress
  // like running, verifying, etc..."
  //
  // A build crosses a PBX, a device cloud and a carrier and takes about ten seconds; this dialog
  // showed one word until the last byte arrived. The build endpoint now narrates itself over NDJSON —
  // `running` per step as it starts, the outcome as it lands, `verifying` once when the read-backs
  // begin — and every line comes from the code doing the work. A client animating a plausible
  // sequence would be inventing the one thing an operator is watching for.
  const [live, setLive] = useState(null);   // { steps: {id → {status, reason, error}}, phase }

  /* `consented` is declared below because it reads `plan.data` (see the note there). These bodies
     only run on click, long after the render that defines it — the rule stays on to catch the
     synchronous case, which this is not. */
  /* eslint-disable no-use-before-define */
  const run = useMutation({
    mutationFn: async () => {
      // TEARDOWN STAYS ON THE PLAIN ENDPOINT. It has no streaming route, and giving it a fake
      // progress bar would be the fiction this exists to avoid.
      if (teardown) {
        return api.post(`/lines/${line.id}/deprovision`, { allowed_systems: consented },
          undefined, { timeoutMs: RUN_TIMEOUT_MS });
      }

      setLive({ steps: {}, phase: 'running' });
      let final = null;
      let failure = null;
      await api.postStream(
        `/lines/${line.id}/provision`,
        // 'verso' is added server-side; sending it would imply it was optional.
        { allowed_systems: consented, stream: true },
        (ev) => {
          if (ev.type === 'running') {
            setLive((s) => ({ ...s, steps: { ...s.steps, [ev.id]: { status: 'running' } } }));
          } else if (ev.type === 'step') {
            setLive((s) => ({
              ...s,
              steps: { ...s.steps, [ev.id]: { status: ev.status, reason: ev.reason, error: ev.error } },
            }));
          } else if (ev.type === 'verifying') {
            setLive((s) => ({ ...s, phase: 'verifying' }));
          } else if (ev.type === 'done') {
            final = ev.result;
          } else if (ev.type === 'error') {
            failure = ev;
          }
        },
      );

      if (failure) {
        const err = new Error(failure.error || 'The run was refused');
        err.status = 409;
        err.body = failure;
        throw err;
      }
      // The stream ended with no verdict. Not a success, and not silently treated as one: the run may
      // well have done work, which is precisely why this has to be said out loud.
      if (!final) throw new Error('The run ended without reporting a result — check the line before running it again.');
      return final;
    },
    onMutate: () => { setRanPlan(plan.data || null); setLive(null); },
    onSuccess: (res) => {
      setResult(res);
      // Everything about this line may have moved.
      qc.invalidateQueries({ queryKey: ['subscriber'] });
      qc.invalidateQueries({ queryKey: ['line', line.id] });
      onDone?.(res);
    },
  });
  /* eslint-enable no-use-before-define */

  const p = plan.data;

  // CONSENT FOLLOWS THE PLAN, not the default.
  //
  // `allowed` starts all-on, so a system the plan does not touch still arrived here ticked — and
  // went out in `allowed_systems` too. Kevin, 2026-08-21, on Daniel Mckenna's build: the dialog
  // listed "Device (GDMS)" under "Systems to write to" while its own notes said "GS4220E ... is
  // provisioned by its own cloud, not GDMS, so this run does not configure it". A tick claiming a
  // write to a system the same dialog declines to touch.
  //
  // It never fired — there were no GDMS steps to run — which is exactly why it could sit there. A
  // consent box that is ticked for something that will not happen teaches an operator that the
  // boxes do not mean anything, and these are the boxes standing between a plan and somebody's
  // live phone system.
  //
  // Sits below `plan` deliberately: it reads `plan.data`, and declaring it above the query put
  // the whole dialog in a temporal dead zone. Until the plan loads nothing is consented, which
  // is correct — nothing can be run either.
  const planTouches = (sys) => (plan.data?.steps || []).some((st) => st.system === sys);
  const consented = Object.keys(allowed).filter((k) => allowed[k] && planTouches(k));
  // WHETHER THERE IS ANYTHING TO CONSENT TO AT ALL, which is not the same question as whether
  // anything is ticked. A plan with no external steps — a teardown of a line that was never
  // built, say — consents to nothing because there is nothing to consent to, and it must still
  // be runnable: the Verso-side work is real. Gating on `!consented.length` alone disabled the
  // button for exactly those plans (caught by LinesTab's teardown tests, whose plan is `steps: []`).
  const hasExternalSteps = ['pbx', 'gdms', 'commio'].some(planTouches);
  const blockers = p?.blockers || [];
  const blocked = Boolean(p && p.ok === false && blockers.length);
  const number = line.number || line.label || `line ${line.id}`;

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:p-8">
      <div className="w-full max-w-2xl rounded-lg bg-white shadow-xl">
        <div className="border-b border-gray-200 px-5 py-4">
          <h2 className="text-base font-semibold text-gray-900">
            {teardown ? 'Take down' : 'Build'} {number}
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            {teardown
              ? 'Removes this line from its PBX, its phone and the carrier’s feature set. '
                + 'The line and its number stay in Verso — this does not delete either.'
              // Plural, and deliberately vague about the destination: a line can ring one
              // extension or five, and its number can reach an extension or a ring group.
              // The old singular copy described a shape the planner outgrew.
              : 'Creates this line’s extensions on the PBX, points the number where it '
                + 'should go, tells each phone what to load, and switches on what the '
                + 'package includes.'}
          </p>
        </div>

        <div
          role="group"
          aria-label={result ? 'Run result' : 'Plan'}
          className="max-h-[60vh] overflow-y-auto px-5 py-4"
        >
          {plan.isLoading && <LoadingSpinner />}
          {plan.error && (
            <p className="text-sm text-red-700">
              Could not work out what to do: {plan.error.body?.error || plan.error.message}
            </p>
          )}

          {/* Frozen? Say it before anything else, so nobody reads a full plan and then
              wonders why every step came back ⊘. */}
          {p?.freeze && !result && (
            <div className="mb-4 rounded-md border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-700">
              <span className="font-medium">Changes save in Verso only.</span>{' '}
              {p.freeze.detail || 'External writes are frozen.'}
            </div>
          )}

          {/* BLOCKERS FIRST. A run that cannot start is the most useful thing on screen,
              and each one names what to fix. */}
          {blocked && !result && (
            <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-3">
              <h3 className="text-sm font-semibold text-amber-900">
                {blockers.length === 1 ? 'One thing' : `${blockers.length} things`} to fix first
              </h3>
              <ul className="mt-2 space-y-2">
                {blockers.map((b) => (
                  <li key={b.code} className="text-sm text-amber-900">
                    {b.message}
                    {/* The 911 blocker is the one an operator cannot act on without
                        being sent somewhere, so send them. */}
                    {b.code === 'no_e911_site' && line.locationId && (
                      <a href={`/locations/${line.locationId}`} className="ml-1 font-medium text-amber-900 underline">
                        Open the address
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* The plan, or the outcome once there is one. */}
          {p && !blocked && (
            <StepList
              steps={(result && ranPlan?.steps) || (live && ranPlan?.steps) || p.steps || []}
              result={result}
              live={live}
            />
          )}

          {p?.notes?.length > 0 && !result && (
            <div className="mt-4 border-t border-gray-100 pt-3">
              <h3 className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Worth knowing
              </h3>
              <ul className="mt-1.5 space-y-1">
                {p.notes.map((n) => (
                  <li key={n.code} className="text-sm text-gray-600">{n.message}</li>
                ))}
              </ul>
            </div>
          )}

          {result && <Verdict result={result} teardown={teardown} line={line} mode={mode} planSteps={(ranPlan || plan.data)?.steps || []} />}

          {/* WE STOPPED WAITING IS NOT IT FAILED, and on this dialog the difference is 28
              live writes to a PBX, a carrier and a customer's phones.

              Kevin, 2026-08-13, building line 136: the browser gave up at ten seconds and
              showed "Request timed out — please try again". The run went on to finish every
              one of its 28 steps, verification confirmed, and the audit log recorded it. The
              advice on screen was to do all of it again.

              Aborting a fetch does not cancel a Worker — there is nothing to cancel through —
              so a timeout here says one thing only: nobody is listening any more. What the
              systems hold is a question for the systems, and the page behind this dialog asks
              them. So: no "try again", and the run is treated as in-flight, not failed. */}
          {run.error?.timedOut ? (
            <div className="mt-4 rounded-md border border-amber-300 bg-amber-50 px-3 py-3 text-sm text-amber-900">
              <p className="font-medium">Verso stopped waiting. The run was not cancelled.</p>
              <p className="mt-1">
                Nothing here was undone and nothing was rolled back — this dialog simply gave
                up listening, and the {teardown ? 'teardown' : 'build'} very likely finished
                without it. Do not run it again until you have looked.
              </p>
              <p className="mt-2">
                Close this and read the page: it asks {line.pbxName || 'the host'}, the phones
                and the carrier directly. The{' '}
                <a href="/audit" className="font-medium underline">audit log</a> records every
                step of the run and whether each one landed.
              </p>
              <button
                type="button"
                onClick={() => { onDone?.(null); onClose(); }}
                className="mt-3 rounded-md bg-amber-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-800"
              >
                Close and re-read the systems
              </button>
            </div>
          ) : run.error && (
            <div className="mt-4 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">
              {run.error.body?.error || run.error.message}
              {/* A 409 carries blockers of its own — the state changed between looking
                  and running, which is exactly when this matters. */}
              {(run.error.body?.blockers || []).map((b) => (
                <p key={b.code} className="mt-1">{b.message}</p>
              ))}
            </div>
          )}
        </div>

        {/* Only before the run: afterwards the results say what was touched, and a row of
            live tick boxes over a finished run invites the reading that they still apply. */}
        {!result && p && !blocked && hasExternalSteps && (
          <div role="group" aria-label="Systems to write to" className="border-t border-gray-200 px-5 py-3">
            <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Systems to write to
            </div>
            <div className="mt-2 flex flex-wrap gap-4">
              {['pbx', 'gdms', 'commio'].map((sys) => {
                // Nothing in this plan touches that system — offering a tick for it
                // suggests it would do something.
                const touched = (p.steps || []).some((st) => st.system === sys);
                return (
                  <label
                    key={sys}
                    title={touched ? undefined : 'Nothing in this plan touches it'}
                    className={`flex items-center gap-2 text-sm ${touched ? 'text-gray-700' : 'text-gray-400'}`}
                  >
                    <input
                      type="checkbox"
                      // Untouched reads UNTICKED. Greying the label was not enough: the tick is
                      // the part that says "this will be written to".
                      checked={allowed[sys] && touched}
                      disabled={!touched || run.isPending}
                      onChange={(e) => setAllowed((a) => ({ ...a, [sys]: e.target.checked }))}
                    />
                    {SYSTEM_LABEL[sys]}
                  </label>
                );
              })}
            </div>
            {!consented.length && (
              <p className="mt-2 text-xs text-amber-800">
                With none of them ticked this would write nothing outside Verso.
              </p>
            )}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            {result ? 'Close' : 'Cancel'}
          </button>
          {/* Not offered after a timeout: the run is in flight, and the one thing an operator
              must not do is start a second one over the top of it. */}
          {!result && !run.error?.timedOut && (
            <button
              type="button"
              disabled={blocked || plan.isLoading || run.isPending
                || (hasExternalSteps && !consented.length)}
              onClick={() => run.mutate()}
              className={`rounded-md px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50 ${
                teardown ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {run.isPending
                // The phase, not a generic wait: an operator watching a carrier write wants to know
                // whether it is still writing or already reading back.
                ? (teardown ? 'Taking it down…' : live?.phase === 'verifying' ? 'Verifying…' : 'Building…')
                : (teardown ? 'Take it down' : 'Build this line')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// The steps, grouped by system, with their outcome and their read-back beside them once
// a run has happened. Before that, the same list reads as "what would happen".
function StepList({ steps, result, live }) {
  const outcome = {};
  // The live narration first, then the finished record over the top of it — the record is
  // authoritative, the narration is what fills the ten seconds before it exists.
  for (const [id, o] of Object.entries(live?.steps || {})) outcome[id] = { id, ...o };
  for (const s of (result?.steps || [])) outcome[s.id] = s;

  // WHAT THIS LIST IS FOR: the work. Kevin, 2026-08-20: "only the steps that need run should show up
  // in the build modal", and specifically "the reboot step should just not be listed if it isn't
  // something that is going to run."
  //
  // TWO CASES, and neither is a step an operator should have to read past:
  //
  //   BEFORE A RUN — `reboot-device` is not a step, it is a consequence of the push: it fires only if
  //   the config actually moved, which nothing can know until the push has happened. Listing it with
  //   "only if this run changed it" asks the reader to hold a conditional that resolves itself. The
  //   push line says what happens instead.
  //
  //   AFTER A RUN — a reboot that was not needed comes back `not-needed`, and it used to render as
  //   `skipped`: amber, with the words "not attempted". That is the good outcome — the phone already
  //   held the config, so nobody's call was dropped — dressed as a fault, which is the third time
  //   today a run doing the right thing has reported itself as suspect.
  const shown = steps.filter((s) => {
    if (s.action !== 'reboot-device') return true;
    const o = outcome[s.id];
    if (!o) return false;                       // preview: a consequence, not a step
    return o.status !== 'not-needed';           // after: only when it actually did something
  });
  const check = {};
  for (const c of (result?.verification?.checks || [])) {
    if (c.stepId) (check[c.stepId] ||= []).push(c);
  }

  // BACKSTOP: anything the run reports that the list does not carry gets appended rather than
  // dropped. The frozen plan should already cover it — but "a step executed and no row said so"
  // is the failure this whole component exists to prevent, so it does not depend on that
  // holding. Described by its action, which is all `result.steps` carries.
  const listed = new Set(steps.map((s) => s.id));
  const extra = (result?.steps || [])
    // A reboot that was not needed is not a missing row — it is deliberately absent above.
    .filter((s) => !listed.has(s.id) && s.status !== 'not-needed')
    .map((s) => ({ id: s.id, system: s.system, description: `${s.action} (not in the plan shown)` }));

  const groups = [];
  for (const s of [...shown, ...extra]) {
    const g = groups.find((x) => x.system === s.system);
    if (g) g.steps.push(s); else groups.push({ system: s.system, steps: [s] });
  }

  if (!shown.length) return <p className="text-sm text-gray-600">Nothing to do.</p>;

  return (
    <ol className="space-y-4">
      {groups.map((g) => (
        <li key={g.system}>
          <h3 className="text-xs font-medium uppercase tracking-wide text-gray-500">
            {SYSTEM_LABEL[g.system] || g.system}
          </h3>
          <ul className="mt-1.5 space-y-1.5">
            {g.steps.map((s) => {
              const o = outcome[s.id];
              const style = o ? (STEP_STYLE[o.status] || STEP_STYLE.skipped) : null;
              return (
                <li key={s.id} className="flex gap-2 text-sm">
                  <span className={`w-4 shrink-0 text-center font-semibold ${style?.cls || 'text-gray-300'}`}>
                    {style?.mark || '·'}
                  </span>
                  <div className="min-w-0">
                    <p className="text-gray-900">{s.description}</p>
                    {o ? (
                      <p className={`text-xs ${style.cls}`}>
                        {style.label}
                        {o.reason ? ` — ${o.reason}` : ''}
                      </p>
                    ) : result && (
                      // Before a run every row looks like this and it means "what would
                      // happen". AFTER one it means the run said nothing about this step, which
                      // is not the same as manual and must not read like it.
                      <p className="text-xs text-amber-700">the run did not report on this step</p>
                    )}
                    {o?.error && (
                      <p className="mt-0.5 break-words text-xs text-red-700">{o.error}</p>
                    )}
                    {/* The read-back, on its own line. A write that returned 200 is not a
                        change that happened, and these two claims must never share a
                        pixel. */}
                    {(check[s.id] || []).map((c, i) => (
                      <p key={i} className={`text-xs ${CHECK_STYLE[c.status]?.cls || 'text-gray-500'}`}>
                        {CHECK_STYLE[c.status]?.label || c.status}: {c.describe}
                        {c.detail ? ` — ${c.detail}` : ''}
                      </p>
                    ))}
                  </div>
                </li>
              );
            })}
          </ul>
        </li>
      ))}
    </ol>
  );
}

/**
 * Raise a support ticket about a failed run, from inside the run that failed.
 *
 * Kevin, 2026-09-03: "add a ticket option to the line build modal form when it fails a step."
 *
 * THE POINT IS THAT NOBODY HAS TO RETYPE THE FAILURE. The thing an operator has in front of them
 * is exactly what a ticket needs — the step, the system, the carrier's own words — and a ticket
 * raised from a blank form somewhere else arrives as "build failed for Lee Hall", which is the
 * report that costs a day of asking. The subject and body are pre-filled and editable; the full
 * run goes in the hidden diagnostic context, which only a platform admin ever sees.
 */
function FailureTicket({ line, mode, result, failedSteps }) {
  const first = failedSteps[0] || null;
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [ticketId, setTicketId] = useState(null);

  const create = useMutation({
    mutationFn: (body) => api.post('/support-tickets', body),
    onSuccess: (res) => { setTicketId(res?.id ?? true); setOpen(false); },
  });

  // WHAT THE OPERATOR SEES, and it is the carrier's sentence rather than ours: a 412 saying
  // "Caller ID type and Location type must match" is the whole diagnosis, and paraphrasing it
  // would throw away the only actionable thing in the run.
  const prefill = () => {
    const what = mode === 'teardown' ? 'Line teardown' : 'Line build';
    const subj = `${what} failed on ${line.number || `line ${line.id}`}`
      + (first?.label ? ` — ${first.label}` : '');
    const body = [
      `${what} for ${line.number || `line ${line.id}`} stopped at the first failure.`,
      '',
      ...(first ? [`Step that failed: ${first.label || first.id}`, `Error: ${first.error || '(none reported)'}`] : []),
      ...(failedSteps.length > 1 ? ['', `${failedSteps.length} steps failed in total.`] : []),
      '',
      'What I was trying to do:',
      '',
    ].join('\n');
    setSubject(subj.slice(0, 200));
    setDescription(body);
    setOpen(true);
  };

  // The hidden context a platform admin needs to reproduce it. TRIMMED HERE rather than relying on
  // the server's 32KB cap: that cap replaces the WHOLE blob with a "truncated" marker, so an
  // oversized context arrives carrying nothing at all. Error strings are the only part that can
  // run long, so they are the part that gets cut.
  const context = () => ({
    source: 'line-run-dialog',
    mode,
    line: { id: line.id, number: line.number ?? null, label: line.label ?? null },
    ok: result.ok === true,
    tally: result.tally || null,
    verification: result.verification
      ? { status: result.verification.status, confirmed: result.verification.confirmed,
          mismatched: result.verification.mismatched, unavailable: result.verification.unavailable }
      : null,
    steps: (result.steps || []).slice(0, 40).map((st) => ({
      id: st.id,
      action: st.action ?? null,
      status: st.status,
      reason: st.reason ? String(st.reason).slice(0, 300) : null,
      error: st.error ? String(st.error).slice(0, 600) : null,
    })),
  });

  if (ticketId) {
    return (
      <p className="mt-2 text-xs font-medium">
        {typeof ticketId === 'number' ? `Ticket #${ticketId} raised.` : 'Ticket raised.'}{' '}
        <span className="font-normal opacity-80">The full run detail went with it.</span>
      </p>
    );
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={prefill}
        className="mt-2 rounded-md border border-current/30 bg-white/70 px-2.5 py-1 text-xs font-medium hover:bg-white"
      >
        Raise a ticket for this
      </button>
    );
  }

  return (
    <div className="mt-3 rounded-md border border-gray-300 bg-white p-3 text-left">
      <label className="block text-xs font-medium text-gray-700">Subject</label>
      <input
        aria-label="Subject"
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        maxLength={200}
        className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm text-gray-900"
      />
      <label className="mt-2 block text-xs font-medium text-gray-700">What happened</label>
      <textarea
        aria-label="What happened"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        rows={7}
        className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 font-mono text-xs text-gray-900"
      />
      <p className="mt-1 text-xs text-gray-500">
        Every step and its error is attached automatically — you do not need to paste them in.
      </p>
      {create.isError && (
        <p className="mt-2 text-sm text-red-600">
          {create.error?.body?.error || create.error?.message || 'Could not raise the ticket.'}
        </p>
      )}
      <div className="mt-3 flex justify-end gap-2">
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={!subject.trim() || create.isPending}
          onClick={() => create.mutate({ subject: subject.trim(), description, context: context() })}
          className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {create.isPending ? 'Raising…' : 'Raise ticket'}
        </button>
      </div>
    </div>
  );
}

// One sentence an operator can act on, and never a tick unless everything checked out.
function Verdict({ result, teardown, line, mode, planSteps = [] }) {
  const t = result.tally || {};
  const v = result.verification || {};
  const frozen = t.frozen || 0;
  const failed = t.error || 0;

  // The steps that actually failed, in run order, so the ticket names the FIRST one — which is the
  // cause. Everything after it reads "not attempted", and a ticket titled with one of those
  // describes a consequence.
  //
  // The LABEL comes from the plan, not the outcome: `result.steps` carries ids and actions, and a
  // ticket headed "commio:set-e911-site:13038235515 failed" makes a reader decode a slug to learn
  // it was the 911 registration. The plan's own sentence is already the one on screen.
  // `description` is the field — it is the sentence StepList renders, so the ticket is headed with
  // the same words the operator is looking at.
  const describeOf = new Map(planSteps.map((st) => [st.id, st.description]));
  const failedSteps = (result.steps || [])
    .filter((st) => st.status === 'error')
    .map((st) => ({ ...st, label: st.description || describeOf.get(st.id) || st.action || st.id }));

  const tone = result.ok
    ? 'border-green-300 bg-green-50 text-green-900'
    : failed || v.status === 'mismatch'
      ? 'border-red-300 bg-red-50 text-red-900'
      : 'border-amber-300 bg-amber-50 text-amber-900';

  return (
    <div className={`mt-4 rounded-md border px-3 py-2 text-sm ${tone}`}>
      <p className="font-medium">
        {result.ok
          ? (teardown ? 'Taken down, and every check confirmed it.'
            : (t['not-selected'] || 0) > 0
              // A partial run that did everything asked of it is a success, and saying only "Built"
              // over six untouched steps overstates it in the other direction.
              ? 'Everything in this run landed, and every check confirmed it.'
              : 'Built, and every check confirmed it.')
          : frozen
            ? 'Nothing was sent.'
            : failed
              ? 'It stopped partway.'
              : 'It ran, but not everything could be confirmed.'}
      </p>
      {/* The freeze sentence comes from the server so the UI cannot soften it. */}
      {result.frozenNotice && <p className="mt-1">{result.frozenNotice}</p>}
      {!result.ok && !frozen && failed > 0 && (
        <>
          <p className="mt-1">
            The run stops at the first failure on purpose — nothing after it was attempted,
            so there is no half-finished state to unpick. Fix the error above and run it again;
            the steps are safe to repeat.
          </p>
          {/* Offered ONLY where a step actually failed. Not after a freeze (nothing was sent, so
              there is nothing to report) and not after an unverified run (the work landed and a
              check could not be taken — a different conversation from a failure). */}
          {line && <FailureTicket line={line} mode={mode} result={result} failedSteps={failedSteps} />}
        </>
      )}
      {!result.ok && !frozen && !failed && v.unavailable > 0 && (
        <p className="mt-1">
          {v.unavailable === 1 ? 'One check' : `${v.unavailable} checks`} could not be taken, so
          this is not being called done. Not being able to look is never a pass.
        </p>
      )}
      <p className="mt-1 text-xs opacity-80">
        {v.confirmed || 0} confirmed · {v.mismatched || 0} disagreed · {v.unavailable || 0} unchecked
        {/* Say out loud that a scoped run was scoped. Without it, "3 confirmed" over six grey rows
            invites the question Kevin asked — why didn't it finish — about a run that finished. */}
        {(t['not-selected'] || 0) > 0 && ` · ${t['not-selected']} not included in this run`}
      </p>
    </div>
  );
}
